<template>
  <v-expansion-panel :disabled="!smartChargingStatus">
    <v-expansion-panel-header disable-icon-rotate>
      {{ expansionPanelTitle }}
      <template v-slot:actions>
        {{ $t('smart-charging-plan.show-more') }}
      </template>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <div class="py-2">{{ planStateDescription }}</div>
      <charging-plan-visualisation :smart-charging-status="smartChargingStatus"
                                   :smart-charging-policy="smartChargingPolicy"
                                   v-if="!isConsidering && smartChargingStatus"/>
      <span v-else>
        {{ descriptionConsidering }}
      </span>
      <v-expansion-panels v-show="!isConsidering && smartChargingStatus">
        <v-expansion-panel>
          <v-expansion-panel-header>
            {{ $t('smart-charging-plan.details') }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-list>
              <v-list-item v-for="(keyTimestamp, index) in keyTimestamps" v-bind:key="index">
                <v-list-item-content>
                  <v-list-item-title>{{ keyTimestamp }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>

import formats from "@/scripts/formats";
import ChargingPlanVisualisation from "@/templates/components/emobility/ChargingPlanVisualisation.vue";
import store from "enode-lib/src/store"
import {createEnodeLib} from "@/plugins/enodePlugin";

export default {
  name: "SmartChargingPlan",
  components: {ChargingPlanVisualisation},
  props: ['vehicleId'],
  data() {
    return {
      states: {
        CONSIDERING: 'CONSIDERING',
        STOPPING: 'PLAN:EXECUTING:STOPPING',
        STARTING: 'PLAN:EXECUTING:STARTING',
        OVERRIDDEN: 'PLAN:EXECUTING:OVERRIDDEN',
        AWAITING_PRICES: 'PLAN:EXECUTING:STOPPED:AWAITING_PRICES',
        STOPPED: 'PLAN:EXECUTING:STOPPED',
        STARTED: 'PLAN:EXECUTING:STARTED',
        FINISHED: 'PLAN:ENDED:FINISHED',
        UNPLUGGED: 'PLAN:ENDED:UNPLUGGED',
        FAILED: 'PLAN:ENDED:FAILED',
        DEADLINE_CHANGED: 'PLAN:ENDED:DEADLINE_CHANGED',
        DISABLED: 'PLAN:ENDED:DISABLED',
        INTERRUPTED: 'PLAN:EXECUTING:CHARGE_INTERRUPTED',
      },
      store: store,
      enodePlugin: createEnodeLib(),
    }
  },
  methods: {
    formatDateTime(dateTime) {
      return formats.formatDateString(dateTime, this.$t('app.date-time-format.long'))
    },
    formatDateForTime(dateTime) {
      return formats.formatDateString(dateTime, this.$t('app.date-time-format.time-only'))
    },
    formatTime(time) {
      let hours = Math.floor(time / 60);
      let minutes = Math.floor(time % 60);
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    },
  },
  computed: {
    isConsidering() {
      return this.smartChargingStatus?.state === this.states.CONSIDERING;
    },
    planFinished() {
      return !!this.smartChargingStatus?.plan?.endedAt;
    },
    expansionPanelTitle() {
      if (this.isConsidering) {
        return this.$t('smart-charging-plan.title.considering');
      } else if (this.planFinished) {
        return this.$t('smart-charging-plan.title.finished', {date: this.formatDateTime(this.smartChargingStatus?.plan?.endedAt)});
      } else if (this.smartChargingStatus?.plan.estimatedFinishAt) {
        return this.$t('smart-charging-plan.title.estimated-finish-at', {date: this.formatDateTime(this.smartChargingStatus?.plan?.estimatedFinishAt)});
      }
      return this.$t('smart-charging-plan.title.initial');
    },
    planStateDescription() {
      if (!this.smartChargingStatus) {
        return null
      }
      switch (this.smartChargingStatus.state) {
        case this.states.STOPPING:
        case this.states.STOPPED:
          return this.$t('smart-charging-plan.state.charging-will-resume', {
            startAt: this.formatDateForTime(this.smartChargingStatus?.plan?.startAt),
            endAt: this.formatDateForTime(this.smartChargingStatus?.plan?.estimatedFinishAt)
          });
        case this.states.STARTING:
        case this.states.STARTED:
          return this.$t('smart-charging-plan.state.will-finish-at', {finishAt: this.formatDateForTime(this.smartChargingStatus?.plan?.estimatedFinishAt)});
        case this.states.OVERRIDDEN:
          return this.$t('smart-charging-plan.state.overridden');
        case this.states.AWAITING_PRICES:
          return this.$t('smart-charging-plan.state.awaiting-prices', {deadline: this.smartChargingPolicy?.deadline});
        case this.states.FINISHED:
          return this.$t('smart-charging-plan.state.finished');
        case this.states.UNPLUGGED:
          return this.$t('smart-charging-plan.state.unplugged');
        case this.states.FAILED:
          return this.$t('smart-charging-plan.state.failed');
        case this.states.DEADLINE_CHANGED:
          return this.$t('smart-charging-plan.state.deadline-changed');
        case this.states.DISABLED:
          return this.$t('smart-charging-plan.state.disabled');
        case this.states.INTERRUPTED:
          return this.$t('smart-charging-plan.state.interrupted');
        default:
          return null
      }
    },
    descriptionConsidering() {
      if (!this.isConsidering) {
        return null
      }
      if (!this.smartChargingStatus?.consideration?.atChargingLocation) {
        return this.$t('smart-charging-plan.consideration.not-at-charging-location');
      }
      if (!this.smartChargingStatus?.consideration?.isPluggedIn) {
        return this.$t('smart-charging-plan.consideration.not-plugged-in');
      }
      if (!this.smartChargingStatus?.consideration?.hasTimeEstimate) {
        return this.$t('smart-charging-plan.consideration.no-time-estimate');
      }
      if (!this.smartChargingStatus?.consideration?.isCharging) {
        return this.$t('smart-charging-plan.consideration.not-charging');
      }
      return null
    },
    keyTimestamps() {
      let keyTimestamps = [];
      if (!this.smartChargingStatus?.plan) {
        return keyTimestamps
      }
      if (this.smartChargingStatus?.plan?.stopConfirmedAt) {
        keyTimestamps.push(this.$t('smart-charging-plan.stop-confirmed-at', {date: this.formatDateTime(this.smartChargingStatus?.plan?.stopConfirmedAt)}));
      }
      if (this.smartChargingStatus?.plan?.startConfirmedAt) {
        keyTimestamps.push(this.$t('smart-charging-plan.start-confirmed-at', {date: this.formatDateTime(this.smartChargingStatus?.plan?.startConfirmedAt)}));
      } else if (this.smartChargingStatus?.plan?.startAt) {
        keyTimestamps.push(this.$t('smart-charging-plan.start-at', {date: this.formatDateTime(this.smartChargingStatus?.plan?.startAt)}));
      }
      if (this.smartChargingStatus?.plan.endedAt) {
        keyTimestamps.push(this.$t('smart-charging-plan.ended-at', {date: this.formatDateTime(this.smartChargingStatus?.plan?.endedAt)}));
        let chargeTime = Math.abs(new Date(this.smartChargingStatus?.plan?.endedAt) - new Date(this.smartChargingStatus?.plan?.startConfirmedAt)) / 60000;
        chargeTime = this.formatTime(chargeTime)
        keyTimestamps.push(this.$t('smart-charging-plan.charge-time-finished', {time: chargeTime}));
      } else if (this.smartChargingStatus?.plan.estimatedFinishAt) {
        keyTimestamps.push(this.$t('smart-charging-plan.estimated-finish-at', {date: this.formatDateTime(this.smartChargingStatus?.plan?.estimatedFinishAt)}));
        let chargeTime = Math.abs(new Date(this.smartChargingStatus?.plan?.estimatedFinishAt) - new Date(this.smartChargingStatus?.plan?.startConfirmedAt ? this.smartChargingStatus?.plan?.startConfirmedAt : this.smartChargingStatus?.plan?.startAt)) / 60000;
        chargeTime = this.formatTime(chargeTime)
        keyTimestamps.push(this.$t('smart-charging-plan.charge-time-estimated', {time: chargeTime}));
      }
      if (this.smartChargingStatus?.plan?.nonSmartCost && this.smartChargingStatus?.plan?.smartCost) {
        let savings = this.smartChargingStatus?.plan?.nonSmartCost - this.smartChargingStatus?.plan?.smartCost;
        savings = savings.toFixed(2);
        let percent = (1 - (this.smartChargingStatus?.plan?.smartCost / this.smartChargingStatus?.plan?.nonSmartCost)) * 100;
        percent = Math.round(percent);
        keyTimestamps.push(this.$t('smart-charging-plan.savings', {savings: savings, percent: percent}));
      }

      return keyTimestamps;
    },
    smartChargingPolicy() {
      return this.store.sharedState.smartChargingPolicies.find(p => p.id === this.vehicleId)?.policy || {
        minimumChargeLimit: 0,
        deadline: "",
        isEnabled: false,
      }
    },
    smartChargingStatus() {
      return this.store.sharedState.smartChargingStatuses[this.vehicleId]
    },
  }
}
</script>